import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { CoreConfigService } from '@core/services/config.service';
import { Subject, takeUntil } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { CustomerSettingService } from './models/customer-setting.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private jwtHelper: JwtHelperService,
    private router: Router,
    public _coreConfigService: CoreConfigService,
    private _title: Title,
    private customerSettingService: CustomerSettingService
  ) {
    this._unsubscribeAll = new Subject();
  }
  private _unsubscribeAll: Subject<any>;
  coreConfig: any;

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
  /** sadece token’ı kontrol eder, redirect yapmaz */
  async checkToken(): Promise<boolean> {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;

    let decoded: any;
    try {
      decoded = this.jwtHelper.decodeToken(token);
    } catch {
      return false;
    }
    if (!decoded || typeof decoded !== 'object') return false;

    const expDate = this.jwtHelper.getTokenExpirationDate(token);
    if (!expDate) return false;

    // eğer expiration server saatiyle karışıyorsa offset ekleyebilirsin
    if (new Date(expDate).getTime() < Date.now()) {
      return false;
    }

    // geçerliyse global değişkenleri set et
    _isAuthenticated = true;
    _isId = +decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
    _isUserName = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
    _isRole = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    _isUserNameAndLastName = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'];
    _isEndpoints = decoded['Endpoint'];
    _isPersonId = +decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/denyonlyprimarysid'];
    _companyName = decoded['CompanyName'];
    _acreditionCompanyId = decoded['AcraditionCompanyId'];


    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;

      if (this.coreConfig?.akraditasyonCompanies) {
        this.coreConfig.akraditasyonCompanies.defaultId = Number(_acreditionCompanyId);
      }

      const firstWord = _companyName?.trim().split(/\s+/)[0] ?? '';

      this.coreConfig.app.appTitle = `${firstWord}-CRM`;
      this.coreConfig.app.appName = `${firstWord}-CRM`;
      this.coreConfig.certificateFirstNo = config.certificateFirstNo;
      this._unsubscribeAll = new Subject();

    });

    this._coreConfigService.setConfig(this.coreConfig);
    this._title.setTitle(this.coreConfig.app.appTitle);

    return true;
  }

  /** korumalı rota için: token geçerli değilse login’e yönlendirir */
  async requireAuth(returnUrl?: string): Promise<boolean> {
    const ok = await this.checkToken();
    if (!ok) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      this.router.navigate(
        ['/pages/authentication/login-v2'],
        returnUrl ? { queryParams: { returnUrl } } : {}
      );
    }
    return ok;
  }

  /** APP_INITIALIZER için alias, redirect yapmaz */
  identityCheck(): Promise<boolean> {
    return this.checkToken();
  }

  get isAuthenticated(): boolean {
    return _isAuthenticated;
  }
  get isId(): number {
    return _isId;
  }
  get isUserName(): string {
    return _isUserName;
  }
  get isUserNameAndLastName(): string {
    return _isUserNameAndLastName;
  }
  get isRole(): string {
    return _isRole;
  }
  get isEndpoints(): any[] {
    return _isEndpoints;
  }
  get isEndpointStatus(): (endpoint: any) => boolean {
    return (endpoint: any) => _isId === 1 ? true : _isEndpoints?.includes(endpoint);
  }
  get isPersonId(): number {
    return _isPersonId;
  }
  get acreditionCompanyId() {
    return _acreditionCompanyId;
  }
}

export let _isAuthenticated = false;
export let _isId: number;
export let _isUserName: string;
export let _isUserNameAndLastName: string;
export let _isRole: string;
export let _isEndpoints: any[];
export let _isPersonId: number;
export let _companyName: string;
export let _acreditionCompanyId: string;
